import { Alert, Button, Checkbox, Drawer, Empty, Form, Grid, Input, Radio, Space, Table, Typography } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, errorLines } from "../app/problems.ts";

type Course = components["schemas"]["CourseDetails"];
type Subject = components["schemas"]["SubjectDetails"];
type Kind = components["schemas"]["SubjectKind"];

export const grades = [5, 6, 7, 8, 9, 10, 11, 12];
const kinds: Kind[] = ["Main", "Secondary"];

interface CourseForm {
  name: string;
  allowedGrades: number[];
}

interface Props {
  open: boolean;
  course?: Course | undefined;
  subjects: Subject[];
  onClose(): void;
  onSaved(course: Course, created: boolean): void;
}

/** Create or edit a course: name, grade checkboxes 5–12 and the subject matrix (include + Main/Secondary per subject). */
export function CourseDrawer({ open, course, subjects, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CourseForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  // Subject id → kind for subjects included in the course; absent means not included.
  const [matrix, setMatrix] = useState<Record<string, Kind>>(() => Object.fromEntries((course?.subjects ?? []).map((s) => [s.subjectId, s.kind])));

  const mutation = useMutation({
    mutationFn: async (values: CourseForm) => {
      const body = {
        name: values.name.trim(),
        allowedGrades: [...values.allowedGrades].sort((a, b) => a - b),
        subjects: Object.entries(matrix).map(([subjectId, kind]) => ({ subjectId, kind })),
      };
      const result = course
        ? await api.PUT("/academic/courses/{id}", { params: { path: { id: course.id } }, body })
        : await api.POST("/academic/courses", { body });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["academic", "courses"] });
      setServerErrors([]);
      onSaved(saved, !course);
    },
    onError: (err) => setServerErrors(errorLines(err, t("app.error"))),
  });

  const toggle = (subjectId: string, included: boolean) =>
    setMatrix((current) => {
      const next = { ...current };
      if (included) next[subjectId] = next[subjectId] ?? "Main";
      else delete next[subjectId];
      return next;
    });

  const included = Object.keys(matrix).length;
  const mainCount = Object.values(matrix).filter((k) => k === "Main").length;

  const columns = [
    {
      title: t("courses.subjects"),
      key: "subject",
      render: (_: unknown, subject: Subject) => (
        <Checkbox checked={subject.id in matrix} onChange={(e) => toggle(subject.id, e.target.checked)}>
          {subject.name}
        </Checkbox>
      ),
    },
    {
      title: t("subjects.rateGroup"),
      key: "rateGroup",
      width: 140,
      responsive: ["sm" as const],
      render: (_: unknown, subject: Subject) => <Typography.Text type="secondary">{t(`subjects.rateGroups.${subject.rateGroup}`)}</Typography.Text>,
    },
    {
      title: t("academicYears.nonSchoolDays.kind"),
      key: "kind",
      width: 220,
      render: (_: unknown, subject: Subject) => (
        <Radio.Group
          size="small"
          optionType="button"
          buttonStyle="solid"
          disabled={!(subject.id in matrix)}
          value={matrix[subject.id]}
          onChange={(e) => setMatrix((current) => ({ ...current, [subject.id]: e.target.value as Kind }))}
          options={kinds.map((k) => ({ value: k, label: t(`courses.kinds.${k}`) }))}
        />
      ),
    },
  ];

  return (
    <Drawer
      title={course ? t("courses.edit") : t("courses.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 640 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<CourseForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={course ? { name: course.name, allowedGrades: course.allowedGrades.map(Number) } : { allowedGrades: [] }}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item name="name" label={t("courses.name")} rules={[{ required: true, message: t("common.required") }]}>
          <Input maxLength={200} placeholder={t("courses.nameHint")} />
        </Form.Item>
        <Form.Item
          name="allowedGrades"
          label={t("courses.allowedGrades")}
          extra={t("courses.allowedGradesHint")}
          rules={[{ validator: (_, value: number[]) => (value?.length ? Promise.resolve() : Promise.reject(new Error(t("courses.validation.grades")))) }]}
        >
          <Checkbox.Group options={grades.map((g) => ({ value: g, label: t("courses.grade", { grade: g }) }))} />
        </Form.Item>
      </Form>

      <Typography.Title level={5} style={{ marginTop: 8 }}>
        {t("courses.subjects")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t("courses.subjectsHint")}</Typography.Paragraph>
      {subjects.length === 0 ? (
        <Empty description={t("courses.subjectsEmpty")} />
      ) : (
        <Space orientation="vertical" size="small" style={{ width: "100%" }}>
          <Table<Subject> rowKey="id" size="small" columns={columns} dataSource={subjects} pagination={false} scroll={{ x: 420 }} />
          <Space separator={<Typography.Text type="secondary">—</Typography.Text>}>
            <Typography.Text type="secondary">{t("courses.mainCount", { count: mainCount })}</Typography.Text>
            <Typography.Text type="secondary">{t("courses.secondaryCount", { count: included - mainCount })}</Typography.Text>
          </Space>
        </Space>
      )}
    </Drawer>
  );
}
